import React from 'react';
import { QuestionOptions } from '../services/api';

interface AdvancedSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  options: Partial<QuestionOptions>;
  onOptionsChange: (options: Partial<QuestionOptions>) => void;
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  isOpen,
  onClose,
  options,
  onOptionsChange
}) => {
  if (!isOpen) return null;

  const updateOption = (key: keyof QuestionOptions, value: any) => {
    onOptionsChange({ ...options, [key]: value });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-tl-sheet rounded-tr-lg rounded-b-lg shadow-lift max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ie-rule">
          <h2 className="text-xl font-semibold text-ie-navy">Advanced Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-ie-slate hover:text-ie-navy hover:bg-ie-mist rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Query Processing */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-ie-navy">Query Processing</h3>
              
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={options.enable_query_rewriting ?? true}
                  onChange={(e) => updateOption('enable_query_rewriting', e.target.checked)}
                  className="rounded border-ie-navy accent-ie-navy"
                />
                <div>
                  <span className="text-sm font-medium text-ie-navy">Query Rewriting</span>
                  <p className="text-xs text-ie-slate">Automatically rephrase queries for better results</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={options.enable_confidence_scoring ?? true}
                  onChange={(e) => updateOption('enable_confidence_scoring', e.target.checked)}
                  className="rounded border-ie-navy accent-ie-navy"
                />
                <div>
                  <span className="text-sm font-medium text-ie-navy">Confidence Scoring</span>
                  <p className="text-xs text-ie-slate">Score answer quality and reliability</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={options.enable_abstention ?? true}
                  onChange={(e) => updateOption('enable_abstention', e.target.checked)}
                  className="rounded border-ie-navy accent-ie-navy"
                />
                <div>
                  <span className="text-sm font-medium text-ie-navy">Smart Abstention</span>
                  <p className="text-xs text-ie-slate">AI will decline to answer when uncertain</p>
                </div>
              </label>
            </div>
            
            {/* Performance Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-ie-navy">Performance</h3>
              
              <div>
                <label className="block text-sm font-medium text-ie-navy mb-2">
                  Confidence Threshold: {((options.confidence_threshold ?? 0.30) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.1"
                  value={options.confidence_threshold ?? 0.30}
                  onChange={(e) => updateOption('confidence_threshold', parseFloat(e.target.value))}
                  className="w-full h-2 bg-ie-rule rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-ie-slate mt-1">
                  <span>Low (10%)</span>
                  <span>High (90%)</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ie-navy mb-2">Search Strategy</label>
                <select
                  value={options.search_strategy ?? 'hybrid'}
                  onChange={(e) => updateOption('search_strategy', e.target.value)}
                  className="w-full text-sm bg-white border border-ie-navy rounded-lg px-3 py-2"
                >
                  <option value="hybrid">Hybrid (Balanced)</option>
                  <option value="semantic">Semantic (Meaning-based)</option>
                  <option value="keyword">Keyword (Exact matches)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ie-navy mb-2">Maximum Agents</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={options.top_k_agents ?? 5}
                  onChange={(e) => updateOption('top_k_agents', parseInt(e.target.value))}
                  className="w-full text-sm bg-white border border-ie-navy rounded-lg px-3 py-2"
                />
                <p className="text-xs text-ie-slate mt-1">Number of document agents to query</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-ie-navy mb-2">Timeout (seconds)</label>
                <input
                  type="number"
                  min="10"
                  max="300"
                  value={options.timeout ?? 60}
                  onChange={(e) => updateOption('timeout', parseInt(e.target.value))}
                  className="w-full text-sm bg-white border border-ie-navy rounded-lg px-3 py-2"
                />
                <p className="text-xs text-ie-slate mt-1">Maximum time to wait for response</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-ie-rule bg-ie-pale">
          <button
            onClick={() => {
              // Reset to defaults
              onOptionsChange({
                enable_query_rewriting: true,
                enable_confidence_scoring: true,
                enable_abstention: true,
                confidence_threshold: 0.30,
                search_strategy: 'hybrid',
                top_k_agents: 5,
                timeout: 60
              });
            }}
            className="px-5 h-10 text-sm font-condensed font-medium uppercase tracking-wide text-ie-navy hover:bg-ie-mist bg-white border border-ie-navy rounded-pill transition-colors"
          >
            Reset Defaults
          </button>
          <button
            onClick={onClose}
            className="px-5 h-10 text-sm font-condensed font-medium uppercase tracking-wide text-white bg-ie-navy hover:bg-ie-sky hover:text-ie-navy border border-ie-navy rounded-pill transition-colors duration-300"
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;